import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  ArrowUpRight,
  Download,
  ExternalLink,
  Lock,
  Calendar,
} from "lucide-react";
import { PageHero, SectionEyebrow } from "@/components/ui-bits";
import { NEWSLETTER_ISSUES } from "@/data/newsletterIssues";
import { PermanentDisclaimer } from "@/components/NewsletterDisclaimer";
import { useAuth } from "@/context/AuthContext";
import { NewsletterSignupModal } from "@/components/NewsletterSignupModal";

const SITE_URL = "https://ogonotary.com";

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}

export default function NewsletterArchivePage() {
  const canonical = `${SITE_URL}/newsletter`;
  const { isAuthed } = useAuth();
  const [signupOpen, setSignupOpen] = React.useState(false);

  const sorted = [...NEWSLETTER_ISSUES].sort((a, b) =>
    (b.publishedAt || "").localeCompare(a.publishedAt || "")
  );

  return (
    <>
      <Helmet>
        <title>OGO Weekly Notary Brief Archive | OGO Notary</title>
        <meta
          name="description"
          content="Explore previous editions of the OGO Weekly Notary Brief. Each issue contains a national update, a featured state, a notarial service topic, a client-growth strategy, and practical small-business education."
        />
        <link rel="canonical" href={canonical} />
      </Helmet>

      <PageHero
        eyebrow="Archive"
        title="OGO Weekly Notary Brief Archive"
        lead="Explore previous editions of the OGO Weekly Notary Brief. Each issue contains a national update, a featured state, a notarial service topic, a client-growth strategy, and practical small-business education."
      />

      {!isAuthed && (
        <section
          data-testid="archive-member-banner"
          className="border-b border-white/10 bg-[#D4AF37]"
        >
          <div className="max-w-5xl mx-auto px-6 lg:px-12 py-4 flex flex-col sm:flex-row items-center justify-between gap-3 text-center sm:text-left">
            <p className="text-[#142339] text-xs sm:text-sm font-bold uppercase tracking-[0.18em]">
              Members get full archive access · Newsletter subscribers get each new issue by email
            </p>
            <button
              type="button"
              onClick={() => setSignupOpen(true)}
              data-testid="archive-subscribe-cta"
              className="inline-flex items-center gap-2 bg-[#142339] text-[#D4AF37] hover:text-white px-4 py-2 text-[10px] uppercase tracking-[0.22em] font-bold transition-colors"
            >
              Get the Free Issue
              <ArrowUpRight className="w-3 h-3" />
            </button>
          </div>
        </section>
      )}

      <section className="py-16 sm:py-20 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <SectionEyebrow className="mb-8">All Issues</SectionEyebrow>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {sorted.map((issue) => (
              <IssueCard key={issue.slug} issue={issue} isAuthed={isAuthed} />
            ))}
          </div>
        </div>
      </section>

      <PermanentDisclaimer />
      <NewsletterSignupModal
        open={signupOpen}
        onClose={() => setSignupOpen(false)}
      />
    </>
  );
}

function IssueCard({ issue, isAuthed }) {
  return (
    <article
      data-testid={`archive-issue-${issue.slug}`}
      className="group border border-white/10 bg-[#1A2B45] hover:border-[#D4AF37]/60 transition-colors flex flex-col overflow-hidden"
    >
      <div className="relative aspect-[4/3] overflow-hidden border-b border-white/10 bg-[#0B1120]">
        <img
          src={issue.cover}
          alt={issue.coverAlt}
          loading="lazy"
          className="w-full h-full object-cover"
        />
        <div className="absolute top-3 left-3 text-[10px] uppercase tracking-[0.22em] bg-[#D4AF37] text-[#142339] font-bold px-2.5 py-1">
          Issue {issue.number}
        </div>
        {!isAuthed && (
          <div className="absolute top-3 right-3 inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.22em] bg-[#0B1120]/90 text-slate-200 border border-white/20 px-2 py-1">
            <Lock className="w-3 h-3 text-[#D4AF37]" />
            Members Only
          </div>
        )}
      </div>
      <div className="p-6 flex flex-col flex-1">
        <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] text-slate-400 mb-3">
          <Calendar className="w-3 h-3" />
          {formatDate(issue.publishedAt)} · {issue.publication}
        </div>
        <h3 className="font-serif-display text-xl text-white leading-tight mb-4">
          {issue.title}
        </h3>
        <dl className="space-y-1.5 text-[11px] mb-6">
          <MetaRow label="State Spotlight" value={issue.stateSpotlight} />
          <MetaRow label="Service Focus" value={issue.serviceFocus} />
          <MetaRow label="Client Growth" value={issue.clientGrowth} />
          <MetaRow label="Business Desk" value={issue.businessDesk} />
        </dl>
        <div className="mt-auto flex flex-col sm:flex-row gap-2">
          <Link
            to={`/newsletter/${issue.slug}`}
            data-testid={`archive-read-${issue.slug}`}
            className="flex-1 inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-[#142339] hover:bg-[#FDE047] px-4 py-2.5 text-[10px] uppercase tracking-[0.22em] font-bold transition-colors"
          >
            <ExternalLink className="w-3 h-3" />
            Read Issue
          </Link>
          <a
            href={issue.pdfUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`archive-pdf-${issue.slug}`}
            className="flex-1 inline-flex items-center justify-center gap-2 border border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37]/10 hover:text-[#FDE047] px-4 py-2.5 text-[10px] uppercase tracking-[0.22em] font-bold transition-colors"
          >
            <Download className="w-3 h-3" />
            PDF
          </a>
        </div>
      </div>
    </article>
  );
}

function MetaRow({ label, value }) {
  return (
    <div className="flex items-baseline gap-2">
      <dt className="text-[9px] uppercase tracking-[0.22em] text-[#D4AF37] font-bold shrink-0 w-[100px]">
        {label}
      </dt>
      <dd className="text-slate-300 flex-1">{value}</dd>
    </div>
  );
}
